// components/SelfPaced/ScormPlayer.js
// ──────────────────────────────────────────────────────────────────────────────
// SCORM 1.2 / 2004 player. Handles single-SCO and multi-SCO packages.
//
// Flow:
//   1. GET  /scorm/lessons/:id/launch  → entryPoint + scos[] + progressByActivity
//   2. POST /scorm/attempt/start       → attemptId + assetToken (+ activityId)
//   3. Mount iframe DIRECTLY at the BE SCO URL
//      (<apiBase>/scorm/assets/:lessonId/:entry?at=<token>). The BE injects
//      scorm-again + a bootstrap into the SCO HTML, so the SCORM API runs in
//      the SCO's own (BE) origin — no cross-origin window.parent lookup. This
//      works even when the FE (custom domain) differs from the BE origin.
//   4. scorm-again (in the SCO) auto-commits every 30s same-origin to the BE.
//   5. On Terminate / LMSFinish OR Save & Exit → SCO posts scorm:finish up to
//      this player → /finish + completion overlay.
//   6. Multi-SCO: clicking a TOC item or Prev/Next remounts the iframe + starts
//      a fresh attempt scoped to the new activityId.
// ──────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, CheckCircle2, Circle, ChevronLeft, ChevronRight,
  ListChecks, Loader2, Maximize2, Minimize2, RefreshCw, Save, CheckCheck, PlayCircle,
} from 'lucide-react';
import { AppButton, ConfirmModal } from '../ui';
import apiClient from '../../utils/apiClient';
import {
  getScormLaunchInfo,
  startScormAttempt,
  finishScormAttempt,
} from '../../utils/scormApi';

// The SCO is loaded directly from the BE asset proxy. The BE injects
// scorm-again + a bootstrap into the SCO HTML, so the SCO runs the SCORM API
// in its own (BE) origin — no cross-origin window.parent lookup. The SCO only
// posts a completion ping back to this player via postMessage.
const API_BASE = (apiClient.defaults.baseURL || '').replace(/\/$/, '');

function originOf(base) {
  try {
    if (!base || base.startsWith('/')) return window.location.origin; // relative → same origin (dev proxy)
    return new URL(base).origin;
  } catch { return window.location.origin; }
}
// Origin the SCO iframe is served from (may differ from the FE in production:
// FE on abc.com, BE on api.lmscampus.com).
const SCO_ORIGIN = originOf(API_BASE);

// Build the SCO launch URL: <apiBase>/scorm/assets/<lessonId>/<entry?params>&at=<token>
function buildScoSrc(lessonId, entry, token) {
  let url = `${API_BASE}/scorm/assets/${lessonId}/${entry}`;
  url += (url.includes('?') ? '&' : '?') + `at=${encodeURIComponent(token)}`;
  return url;
}

// The player's own chrome follows the theme; the package's iframe stays white,
// because SCORM content is authored for a white page.
const COLORS = {
  bg: 'var(--card-bg)',
  toolbarBg: 'var(--bg-tertiary)',
  tocBg: 'var(--bg-tertiary)',
  toolbarBorder: 'var(--border-primary)',
  text: 'var(--text-primary)',
  textMuted: 'hsl(var(--muted-foreground))',
  // Tokens, not literals: these land in inline `style={{}}`, where var() resolves
  // (unlike an SVG presentation attribute). The accent was violet-600 — the old
  // brand, hardcoded, so it could never follow `Institute.primaryColor`.
  brand: 'hsl(var(--primary))',
  brandSoft: 'hsl(var(--primary) / 0.10)',
  // A filled button is the fill pair, never the ink + white (TOKEN_CONTRACT §4).
  brandFill: 'hsl(var(--primary-solid))',
  brandFillFg: 'hsl(var(--primary-solid-fg))',
  success: 'hsl(var(--success-fg))',
  successBg: 'hsl(var(--success-soft))',
  warning: 'hsl(var(--warning-fg))',
  warningBg: 'hsl(var(--warning-soft))',
  danger: 'hsl(var(--danger-fg))',
};

function StatusPill({ phase, packageVersion, edition }) {
  let label = 'Loading…';
  let bg = COLORS.warningBg;
  let fg = COLORS.warning;
  let Dot = Loader2;
  let spin = true;

  const versionLabel = packageVersion
    ? `SCORM ${packageVersion}${edition && edition !== 'unknown' ? ` ${edition} Ed` : ''}`
    : '';

  if (phase === 'cover') {
    label = versionLabel || 'SCORM';
    bg = 'hsl(var(--info-soft))'; fg = 'hsl(var(--info-fg))'; Dot = null; spin = false;
  } else if (phase === 'running') {
    label = versionLabel ? `In progress · ${versionLabel}` : 'In progress';
    bg = 'hsl(var(--info-soft))'; fg = 'hsl(var(--info-fg))'; Dot = null; spin = false;
  } else if (phase === 'finished') {
    label = 'Completed';
    bg = COLORS.successBg; fg = COLORS.success; Dot = CheckCircle2; spin = false;
  } else if (phase === 'error') {
    label = 'Error';
    bg = 'hsl(var(--danger-soft))'; fg = COLORS.danger; Dot = AlertCircle; spin = false;
  }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: bg, color: fg,
      fontSize: 12, fontWeight: 600, letterSpacing: 0.1,
    }}>
      {Dot && <Dot size={14} style={spin ? { animation: 'scorm-spin 1s linear infinite' } : undefined} />}
      {label}
    </span>
  );
}

function IconBtn({ title, onClick, disabled, children, active }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        height: 32, width: 32, padding: 0,
        background: active ? COLORS.brandSoft : 'transparent',
        border: `1px solid ${COLORS.toolbarBorder}`,
        borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
        color: active ? COLORS.brand : COLORS.text,
        opacity: disabled ? 0.5 : 1,
      }}
    >{children}</button>
  );
}

export default function ScormPlayer({ lessonId, onComplete, onNextLesson, hasNextLesson, onFinish }) {
  const wrapperRef = useRef(null);
  const iframeRef = useRef(null);

  const [phase, setPhase] = useState('loading'); // loading | cover | running | error | finished
  const [error, setError] = useState('');
  const [launchInfo, setLaunchInfo] = useState(null);     // lesson + scos + progressByActivity
  const [resumeState, setResumeState] = useState(null);   // { status, progressPct, hasProgress, scosTotal, scosCompleted }
  const [attemptInfo, setAttemptInfo] = useState(null);   // current SCO's attempt
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [tocOpen, setTocOpen] = useState(true);
  const [confirmRetake, setConfirmRetake] = useState(false);
  // Per-SCO completion as we observe it locally during this session (so the
  // TOC tick flips before the next page refresh).
  const [scoStatus, setScoStatus] = useState({});
  // Retake session: once the learner clicks Retake, each SCO they open gets a
  // fresh attempt (tracked so we don't restart the same SCO twice in a session).
  const retakeModeRef = useRef(false);
  const restartedScosRef = useRef(new Set());

  const scos = launchInfo?.lesson?.scorm?.scos || [];
  const isMultiSco = scos.length > 1;
  const currentActivityId = attemptInfo?.activityId || '';
  const currentScoIdx = useMemo(
    () => scos.findIndex((s) => s.activityId === currentActivityId),
    [scos, currentActivityId],
  );
  const packageVersion = launchInfo?.lesson?.scorm?.packageVersion;
  const packageEdition = launchInfo?.lesson?.scorm?.packageEdition;

  // Live progress %:
  //   multi-SCO → completed SCOs / total (updates as the learner finishes SCOs)
  //   single-SCO → resumeState.progressPct, snapped to 100 once finished
  const livePct = useMemo(() => {
    if (isMultiSco) {
      if (!scos.length) return 0;
      const done = scos.filter((s) => scoStatus[s.activityId]?.completed).length;
      return Math.round((done / scos.length) * 100);
    }
    if (phase === 'finished') return 100;
    return resumeState?.progressPct ?? null; // null = unknown (1.2 single-SCO in progress)
  }, [isMultiSco, scos, scoStatus, phase, resumeState]);

  // ── Step 1: fetch launch info ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPhase('loading');
        setError('');
        const r = await getScormLaunchInfo(lessonId);
        if (cancelled) return;
        if (!r?.data?.success) throw new Error(r?.data?.message || 'Launch failed');
        setLaunchInfo(r.data);
        setResumeState(r.data.resumeState || null);
        // Seed scoStatus from server-side progress map (multi-SCO only).
        const seed = {};
        const map = r.data.progressByActivity || {};
        Object.keys(map).forEach((k) => {
          seed[k] = { completed: Boolean(map[k]?.completed), passed: map[k]?.passed ?? null };
        });
        setScoStatus(seed);
        // Show the launch/cover screen (with progress + Resume/Start) instead
        // of auto-launching, so the learner has explicit control.
        setPhase('cover');
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load SCORM');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [lessonId]);

  // ── Start (or switch) attempt for the given SCO ────────────────────────────
  const startAttemptForSco = useCallback(async (activityId, restart = false) => {
    setSwitching(true);
    setPhase('loading');
    try {
      const r = await startScormAttempt(lessonId, {
        ...(activityId ? { activityId } : {}),
        ...(restart ? { restart: true } : {}),
      });
      const started = r?.data;
      if (!started?.success) throw new Error(started?.message || 'Could not start attempt');
      setAttemptInfo({
        attemptId: started.attemptId,
        attemptNumber: started.attemptNumber,
        activityId: started.activityId || '',
        entryPoint: started.entryPoint,
        packageVersion: started.packageVersion,
        cmi12: started.cmi12,
        cmi2004: started.cmi2004,
        suspendData: started.suspendData,
        assetToken: started.assetToken,
      });
      setPhase('running');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to start attempt');
      setPhase('error');
    } finally {
      setSwitching(false);
    }
  }, [lessonId]);

  // ── Launch from the cover screen ───────────────────────────────────────────
  // Resume opens the first not-yet-completed SCO (natural progression); a
  // fresh start opens the first SCO. Single-SCO passes '' (BE picks it).
  const launch = useCallback(() => {
    if (isMultiSco) {
      const firstIncomplete = scos.find((s) => !scoStatus[s.activityId]?.completed);
      const target = (firstIncomplete || scos[0])?.activityId;
      startAttemptForSco(target);
    } else {
      startAttemptForSco('');
    }
  }, [isMultiSco, scos, scoStatus, startAttemptForSco]);

  // ── Step 3: bridge messages with the runtime iframe ────────────────────────
  const handleFinishPayload = useCallback(async (cmi) => {
    if (!attemptInfo) return;
    try {
      setSaving(true);
      const r = await finishScormAttempt(attemptInfo.attemptId, cmi || {});
      if (r?.data?.success) {
        // Locally mark this SCO done so the TOC tick flips immediately.
        setScoStatus((prev) => ({
          ...prev,
          [attemptInfo.activityId || '']: {
            completed: Boolean(r.data.completed),
            passed: r.data.passed ?? null,
          },
        }));
        // For single-SCO OR when the just-finished SCO completes the whole
        // lesson, flip to the completion overlay.
        if (!isMultiSco) {
          setPhase('finished');
          if (typeof onComplete === 'function') onComplete();
        } else {
          // Multi-SCO: only show the lesson-level completion overlay when ALL
          // SCOs are done.
          const localStatus = {
            ...scoStatus,
            [attemptInfo.activityId || '']: { completed: Boolean(r.data.completed), passed: r.data.passed ?? null },
          };
          const allDone = scos.every((s) => localStatus[s.activityId]?.completed);
          if (allDone) {
            setPhase('finished');
            if (typeof onComplete === 'function') onComplete();
          }
        }
      }
    } catch { /* auto-commits already wrote */ }
    finally { setSaving(false); }
  }, [attemptInfo, onComplete, isMultiSco, scos, scoStatus]);

  useEffect(() => {
    // Accept messages from our own origin (dev proxy / same-site) AND from the
    // BE asset origin (production cross-origin SCO). Both are trusted senders.
    const allowed = new Set([window.location.origin, SCO_ORIGIN]);
    function onMessage(e) {
      if (!allowed.has(e.origin)) return;
      const data = e?.data;
      if (!data || typeof data !== 'object') return;
      // The SCO (running scorm-again in its own origin) posts these.
      if (data.type === 'scorm:finish') {
        // The CMI was already committed same-origin by the SCO; finish with an
        // empty body just flips finishedAt + recomputes from stored state.
        handleFinishPayload({});
      }
      // 'scorm:sco-ready' is informational; no action needed.
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleFinishPayload]);

  // ── Fullscreen plumbing ────────────────────────────────────────────────────
  const toggleFullscreen = useCallback(async () => {
    const el = wrapperRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) await el.requestFullscreen?.();
      else                              await document.exitFullscreen?.();
    } catch { /* user cancelled */ }
  }, []);
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ── Save & Exit (manual finish) ────────────────────────────────────────────
  const onSaveExit = useCallback(() => {
    try {
      // Ask the SCO (BE origin) to terminate; it will commit + post scorm:finish.
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'scorm:request-finish' },
        SCO_ORIGIN,
      );
    } catch { /* ignore */ }
    // Fallback: the SCO auto-commits every 30s, so finishing directly is safe.
    setTimeout(() => { handleFinishPayload({}); }, 800);
  }, [handleFinishPayload]);

  // ── Switch SCO (multi-SCO navigation) ──────────────────────────────────────
  const switchToSco = useCallback((activityId) => {
    if (!activityId || activityId === currentActivityId || switching) return;
    // During a retake session, restart each SCO once so the learner re-does
    // the whole package; otherwise resume.
    const shouldRestart = retakeModeRef.current && !restartedScosRef.current.has(activityId);
    if (shouldRestart) restartedScosRef.current.add(activityId);
    // Unmount the current iframe so scorm-again finalises its sendBeacon
    // commit on unload, then start the new SCO's attempt.
    setAttemptInfo(null);
    startAttemptForSco(activityId, shouldRestart);
  }, [currentActivityId, switching, startAttemptForSco]);

  const goPrev = () => { const i = currentScoIdx; if (i > 0) switchToSco(scos[i - 1].activityId); };
  const goNext = () => { const i = currentScoIdx; if (i >= 0 && i < scos.length - 1) switchToSco(scos[i + 1].activityId); };

  // ── Retake the whole lesson (fresh attempt(s)) ─────────────────────────────
  const onRetake = useCallback(() => {
    if (switching) return;
    setScoStatus({});            // clear local completion ticks
    setAttemptInfo(null);
    if (isMultiSco) {
      retakeModeRef.current = true;
      const firstId = scos[0]?.activityId;
      restartedScosRef.current = new Set(firstId ? [firstId] : []);
      startAttemptForSco(firstId, true);
    } else {
      startAttemptForSco('', true);
    }
  }, [switching, isMultiSco, scos, startAttemptForSco]);

  // ── Render ────────────────────────────────────────────────────────────────
  const wrapperStyle = {
    position: 'relative', display: 'flex', flexDirection: 'column',
    height: '100%', minHeight: 480,
    background: COLORS.bg,
    borderRadius: isFullscreen ? 0 : 10,
    overflow: 'hidden',
  };

  const toolbarStyle = {
    flex: '0 0 auto',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 8, padding: '8px 12px',
    background: COLORS.toolbarBg,
    borderBottom: `1px solid ${COLORS.toolbarBorder}`,
  };

  const bodyStyle = {
    flex: '1 1 auto', minHeight: 0,
    display: 'flex',
    background: COLORS.bg,
  };

  const iframeWrapStyle = {
    flex: '1 1 auto', minWidth: 0, position: 'relative',
  };

  const tocStyle = {
    flex: '0 0 280px',
    background: COLORS.tocBg,
    borderLeft: `1px solid ${COLORS.toolbarBorder}`,
    overflow: 'auto',
  };

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <style>{`@keyframes scorm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* ── Toolbar ── */}
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <StatusPill phase={phase} packageVersion={packageVersion} edition={packageEdition} />
          {phase === 'running' && attemptInfo?.attemptNumber > 1 && (
            <span style={{ fontSize: 12, color: COLORS.textMuted }}>
              Attempt #{attemptInfo.attemptNumber}
            </span>
          )}
          {isMultiSco && currentScoIdx >= 0 && (
            <span style={{
              fontSize: 12, color: COLORS.textMuted,
              maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              · {currentScoIdx + 1} / {scos.length} · {scos[currentScoIdx]?.title || scos[currentScoIdx]?.activityId}
            </span>
          )}
          {/* Live progress bar (running) */}
          {phase === 'running' && livePct != null && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
              <span style={{ width: 90, height: 6, borderRadius: 999, background: COLORS.toolbarBorder, overflow: 'hidden' }}>
                <span style={{
                  display: 'block', height: '100%', width: `${livePct}%`,
                  background: livePct >= 100 ? COLORS.success : COLORS.brand,
                  borderRadius: 999, transition: 'width .3s ease',
                }} />
              </span>
              <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 600 }}>{livePct}%</span>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isMultiSco && (
            <>
              <IconBtn title="Previous SCO" onClick={goPrev} disabled={switching || currentScoIdx <= 0}>
                <ChevronLeft size={16} />
              </IconBtn>
              <IconBtn title="Next SCO" onClick={goNext} disabled={switching || currentScoIdx < 0 || currentScoIdx >= scos.length - 1}>
                <ChevronRight size={16} />
              </IconBtn>
              <IconBtn title={tocOpen ? 'Hide contents' : 'Show contents'} onClick={() => setTocOpen((v) => !v)} active={tocOpen}>
                <ListChecks size={16} />
              </IconBtn>
            </>
          )}
          {phase === 'running' && (
            <button
              type="button"
              onClick={onSaveExit}
              disabled={saving || switching}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                height: 32, padding: '0 12px',
                background: COLORS.bg, color: COLORS.text,
                border: `1px solid ${COLORS.toolbarBorder}`, borderRadius: 8,
                fontSize: 13, fontWeight: 500,
                cursor: (saving || switching) ? 'not-allowed' : 'pointer',
                opacity: (saving || switching) ? 0.6 : 1,
              }}
              title="Save current progress and mark this SCO finished. Use if the SCORM package does not provide its own exit button."
            >
              {saving ? <Loader2 size={14} style={{ animation: 'scorm-spin 1s linear infinite' }} /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save & Exit'}
            </button>
          )}
          <IconBtn title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </IconBtn>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={bodyStyle}>
        <div style={iframeWrapStyle}>
          {phase === 'loading' && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12,
              color: COLORS.textMuted,
            }}>
              <Loader2 size={28} style={{ color: COLORS.brand, animation: 'scorm-spin 1s linear infinite' }} />
              <div style={{ fontSize: 13 }}>{switching ? 'Loading next section…' : 'Loading SCORM package…'}</div>
            </div>
          )}

          {phase === 'error' && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center', padding: 24,
            }}>
              <AlertCircle size={32} color={COLORS.danger} />
              <div style={{ maxWidth: 480, color: COLORS.text }}>{error}</div>
              <AppButton variant="outline" size="sm" onClick={() => window.location.reload()}>
                <RefreshCw size={14} /> Retry
              </AppButton>
            </div>
          )}

          {/* ── Launch / cover screen (progress + Resume/Start/Review) ── */}
          {phase === 'cover' && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 16, padding: 24, textAlign: 'center',
            }}>
              <div style={{
                width: '100%', maxWidth: 460,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
                padding: '32px 28px', borderRadius: 14,
                border: `1px solid ${COLORS.toolbarBorder}`, background: COLORS.bg,
                boxShadow: '0 12px 40px -24px rgba(0,0,0,0.25)',
              }}>
                <div style={{
                  height: 52, width: 52, borderRadius: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: COLORS.brandSoft, color: COLORS.brand,
                }}>
                  {resumeState?.status === 'completed'
                    ? <CheckCircle2 size={26} color={COLORS.success} />
                    : <PlayCircle size={26} />}
                </div>
                <div style={{ fontSize: 18, fontWeight: 500, color: COLORS.text }}>
                  {launchInfo?.lesson?.title || 'SCORM lesson'}
                </div>
                <div style={{ fontSize: 13, color: COLORS.textMuted }}>
                  {packageVersion ? `SCORM ${packageVersion}${packageEdition && packageEdition !== 'unknown' ? ` ${packageEdition} Ed` : ''}` : 'SCORM package'}
                  {isMultiSco ? ` · ${scos.length} sections` : ''}
                </div>

                {/* Progress bar — shown whenever there's prior progress */}
                {resumeState?.hasProgress && (
                  <div style={{ width: '100%', marginTop: 4 }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: 12, color: COLORS.textMuted, marginBottom: 6,
                    }}>
                      <span>
                        {resumeState.status === 'completed' ? 'Completed' : 'In progress'}
                        {isMultiSco ? ` · ${resumeState.scosCompleted}/${resumeState.scosTotal} sections` : ''}
                      </span>
                      <span>{resumeState.progressPct == null ? '' : `${resumeState.progressPct}%`}</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: COLORS.toolbarBorder, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: resumeState.progressPct == null ? '40%' : `${resumeState.progressPct}%`,
                        background: resumeState.status === 'completed' ? COLORS.success : COLORS.brand,
                        borderRadius: 999, transition: 'width .3s ease',
                        opacity: resumeState.progressPct == null ? 0.5 : 1,
                      }} />
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={launch}
                  disabled={switching}
                  style={{
                    marginTop: 6,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    height: 42, padding: '0 22px',
                    background: COLORS.brandFill, color: COLORS.brandFillFg,
                    border: 'none', borderRadius: 10,
                    fontSize: 14, fontWeight: 600,
                    cursor: switching ? 'not-allowed' : 'pointer',
                    opacity: switching ? 0.7 : 1,
                  }}
                >
                  {switching ? (
                    <Loader2 size={16} style={{ animation: 'scorm-spin 1s linear infinite' }} />
                  ) : (
                    <PlayCircle size={16} />
                  )}
                  {resumeState?.status === 'in_progress' ? 'Resume'
                    : resumeState?.status === 'completed' ? 'Review again'
                      : 'Start lesson'}
                </button>

                {resumeState?.status === 'completed' && (
                  <button
                    type="button"
                    onClick={() => setConfirmRetake(true)}
                    disabled={switching}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      height: 34, padding: '0 14px',
                      background: 'transparent', color: COLORS.brand,
                      border: 'none', fontSize: 13, fontWeight: 600,
                      cursor: switching ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <RefreshCw size={13} /> Retake{isMultiSco ? ' lesson' : ''}
                  </button>
                )}
              </div>
            </div>
          )}

          {(phase === 'running' || phase === 'finished') && attemptInfo && (
            <iframe
              // key forces a clean unmount/remount when switching SCOs so the
              // previous scorm-again instance fully tears down (sendBeacon
              // commit on unload captures the latest state).
              key={`${attemptInfo.attemptId}:${attemptInfo.activityId}`}
              ref={iframeRef}
              title="SCORM"
              src={buildScoSrc(
                lessonId,
                `${attemptInfo.entryPoint || ''}${(scos.find((s) => s.activityId === currentActivityId)?.parameters) || ''}`,
                attemptInfo.assetToken,
              )}
              // allow= delegates Permissions-Policy features down to media the SCO
              // embeds (e.g. a YouTube <iframe> inside the package) — without
              // encrypted-media/autoplay the embed renders blank. allow-presentation
              // + allow-popups-to-escape-sandbox let "watch on YouTube" links open
              // as a normal, un-sandboxed tab.
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture; clipboard-write"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation allow-modals allow-downloads"
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                border: 0, background: '#fff',
                display: 'block',
              }}
            />
          )}

          {phase === 'finished' && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 12, background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(2px)',
            }}>
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                padding: '28px 36px', borderRadius: 14,
                background: COLORS.bg, boxShadow: '0 20px 50px -20px rgba(0,0,0,0.35)',
                maxWidth: 420, textAlign: 'center',
              }}>
                <CheckCircle2 size={44} color={COLORS.success} />
                <div style={{ fontSize: 18, fontWeight: 600, color: COLORS.text }}>Lesson completed</div>
                <div style={{ fontSize: 13, color: COLORS.textMuted }}>
                  {hasNextLesson
                    ? 'Your progress has been saved. Continue to the next topic or retake this lesson.'
                    : 'Your progress has been saved. You can retake this lesson if you want.'}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setConfirmRetake(true)}
                    disabled={switching}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      height: 38, padding: '0 16px',
                      background: COLORS.bg, color: COLORS.brand,
                      border: `1px solid ${COLORS.brand}`, borderRadius: 8,
                      fontSize: 13, fontWeight: 600,
                      cursor: switching ? 'not-allowed' : 'pointer',
                      opacity: switching ? 0.6 : 1,
                    }}
                    title="Start a fresh attempt. Your previous result stays on record."
                  >
                    <RefreshCw size={14} /> Retake{isMultiSco ? ' lesson' : ''}
                  </button>
                  {hasNextLesson && typeof onNextLesson === 'function' ? (
                    <button
                      type="button"
                      onClick={() => onNextLesson()}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        height: 38, padding: '0 18px',
                        background: COLORS.brandFill, color: COLORS.brandFillFg,
                        border: 'none', borderRadius: 8,
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                      title="Go to the next lesson in this course."
                    >
                      Next topic <ChevronRight size={15} />
                    </button>
                  ) : (
                    typeof onFinish === 'function' && (
                      <button
                        type="button"
                        onClick={() => onFinish()}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          height: 38, padding: '0 18px',
                          background: COLORS.brandFill, color: COLORS.brandFillFg,
                          border: 'none', borderRadius: 8,
                          fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}
                        title="Finish and return to the course."
                      >
                        <CheckCheck size={15} /> Submit &amp; Finish
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── TOC ── */}
        {isMultiSco && tocOpen && (
          <div style={tocStyle}>
            <div style={{
              padding: '12px 14px',
              borderBottom: `1px solid ${COLORS.toolbarBorder}`,
              fontSize: 12, fontWeight: 600, color: COLORS.textMuted, letterSpacing: 0.4, textTransform: 'uppercase',
            }}>
              Course contents · {scos.filter((s) => scoStatus[s.activityId]?.completed).length} / {scos.length}
            </div>
            <div style={{ padding: 8 }}>
              {scos.map((s, idx) => {
                const isActive = s.activityId === currentActivityId;
                const isDone = scoStatus[s.activityId]?.completed;
                return (
                  <button
                    key={s.activityId}
                    type="button"
                    onClick={() => switchToSco(s.activityId)}
                    disabled={switching}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', textAlign: 'left',
                      padding: '8px 10px', borderRadius: 8,
                      background: isActive ? COLORS.brandSoft : 'transparent',
                      color: isActive ? COLORS.brand : COLORS.text,
                      border: 'none',
                      cursor: switching ? 'not-allowed' : 'pointer',
                      fontSize: 13, lineHeight: 1.35,
                      opacity: switching && !isActive ? 0.5 : 1,
                    }}
                    title={s.title || s.activityId}
                  >
                    <span style={{ flex: '0 0 18px', display: 'inline-flex' }}>
                      {isDone
                        ? <CheckCircle2 size={16} color={COLORS.success} />
                        : <Circle size={16} color={isActive ? COLORS.brand : COLORS.textMuted} />}
                    </span>
                    <span style={{ flex: '0 0 22px', color: COLORS.textMuted, fontSize: 11, fontWeight: 600 }}>{idx + 1}.</span>
                    <span style={{
                      flex: '1 1 auto',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      fontWeight: isActive ? 600 : 400,
                    }}>
                      {s.title || s.activityId}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Custom retake confirmation (no native confirm dialogs) */}
      <ConfirmModal
        show={confirmRetake}
        onHide={() => setConfirmRetake(false)}
        onConfirm={() => { setConfirmRetake(false); onRetake(); }}
        title={`Retake ${isMultiSco ? 'lesson' : 'this lesson'}?`}
        message="This starts a fresh attempt with a clean slate. Your previous result stays on record; the latest attempt becomes your current status."
        confirmText="Retake"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
}
